import { useState, useEffect } from "react";
import { Shield, CheckCircle, AlertTriangle, Info, RefreshCw, ExternalLink } from "lucide-react";
import { alertVerificationService, type AlertVerification } from "../utils/alertVerification.ts";

export default function DataTransparencyPanel() {
  const [verificationData, setVerificationData] = useState<AlertVerification[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const fetchVerificationData = async () => {
    setLoading(true);
    try {
      const data = await alertVerificationService.verifyCurrentAlerts();
      setVerificationData(data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Failed to fetch verification data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVerificationData();
  }, []);

  const realCount = verificationData.filter(alert => alert.isRealGovernmentData).length;
  const totalCount = verificationData.length;
  const realPercentage = totalCount > 0 ? Math.round((realCount / totalCount) * 100) : 0;

  const sourceCounts = verificationData.reduce((acc, alert) => {
    const sourceType = alert.isRealGovernmentData ? 'verified' : 'sample';
    acc[sourceType] = (acc[sourceType] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const governmentSources = [
    { name: 'FEWS NET', description: 'Famine Early Warning Systems Network', status: 'active' },
    { name: 'ReliefWeb', description: 'UN Humanitarian Information Service', status: 'active' },
    { name: 'World Bank', description: 'International Development Data', status: 'active' },
    { name: 'Ministry of Agriculture - Lesotho', description: 'Official Government Source', status: 'pending' },
    { name: 'Lesotho Meteorological Services', description: 'National Weather Service', status: 'pending' }
  ];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Shield className="w-6 h-6 text-blue-600" />
          <div>
            <h2 className="text-xl font-semibold text-gray-900">Data Transparency</h2>
            <p className="text-sm text-gray-600">Verify the authenticity of alert sources</p>
          </div>
        </div>
        <button
          onClick={fetchVerificationData}
          disabled={loading}
          className="flex items-center space-x-2 px-3 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors disabled:opacity-50"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Statistics Overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-green-50 p-4 rounded-lg border border-green-200">
          <div className="flex items-center space-x-3">
            <CheckCircle className="w-8 h-8 text-green-600" />
            <div>
              <p className="text-2xl font-bold text-green-800">{realCount}</p>
              <p className="text-sm text-green-600">Verified Government Sources</p>
            </div>
          </div>
        </div>

        <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
          <div className="flex items-center space-x-3">
            <AlertTriangle className="w-8 h-8 text-yellow-600" />
            <div>
              <p className="text-2xl font-bold text-yellow-800">{totalCount - realCount}</p>
              <p className="text-sm text-yellow-600">Sample/Demo Data</p>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
          <div className="flex items-center space-x-3">
            <Info className="w-8 h-8 text-blue-600" />
            <div>
              <p className="text-2xl font-bold text-blue-800">{realPercentage}%</p>
              <p className="text-sm text-blue-600">Real Data Coverage</p>
            </div>
          </div>
        </div>
      </div>

      {/* Data Sources Status */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Connected Data Sources</h3>
        <div className="space-y-3">
          {governmentSources.map((source, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${
                  source.status === 'active' ? 'bg-green-500' : 
                  source.status === 'pending' ? 'bg-yellow-500' : 'bg-red-500'
                }`} />
                <div>
                  <p className="font-medium text-gray-900">{source.name}</p>
                  <p className="text-sm text-gray-600">{source.description}</p>
                </div>
              </div>
              <div className="flex items-center space-x-2">
                <span className={`px-2 py-1 rounded text-xs font-medium ${
                  source.status === 'active' ? 'bg-green-100 text-green-700' :
                  source.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                  'bg-red-100 text-red-700'
                }`}>
                  {source.status.toUpperCase()}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Alerts Verification */}
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Alerts Verification</h3>
        <div className="space-y-2 max-h-64 overflow-y-auto">
          {verificationData.slice(0, 10).map((alert) => (
            <div key={alert.id} className="flex items-center justify-between p-3 bg-gray-50 rounded border border-gray-200">
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">{alert.title}</p>
                <p className="text-xs text-gray-500">
                  {new Date(alert.created_at).toLocaleDateString()} • {alert.source_type.replace(/_/g, ' ')}
                </p>
              </div>
              <div className="flex items-center space-x-2 ml-4">
                {alert.isRealGovernmentData ? (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-green-100 text-green-700 rounded text-xs">
                    <CheckCircle className="w-3 h-3" />
                    <span>VERIFIED</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-1 px-2 py-1 bg-yellow-100 text-yellow-700 rounded text-xs">
                    <AlertTriangle className="w-3 h-3" />
                    <span>SAMPLE</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Footer Info */}
      {lastUpdated && (
        <div className="mt-6 pt-4 border-t border-gray-200">
          <p className="text-xs text-gray-500 text-center">
            Last updated: {lastUpdated.toLocaleString()} • 
            Data verified against official government and international organization sources
          </p>
        </div>
      )}
    </div>
  );
}